import { Box, Flex } from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import { BusyEventContent } from './BusyEventContent';
import { ManualAvailableContent } from './ManualAvailableContent';
import { ManualBookedContent } from './ManualBookedContent';
import { MeetingRoomEventContent } from './MeetingRoomEventContent';
import { SelfFreeSlot } from './SelfFreeSlot';

const formatTimeRangeForDay = (event, eventInfo) => {
  const originalStart = new Date(event.start);
  const originalEnd = new Date(event.end);
  const originalStartDay = originalStart.toDateString();
  const originalEndDay = originalEnd.toDateString();

  // For FullCalendar multi-day events, use the event instance's times
  const instanceStart = new Date(eventInfo.event.start);
  const instanceDay = instanceStart.toDateString();

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Check if original event spans multiple days
  if (originalStartDay !== originalEndDay) {
    if (instanceDay === originalStartDay) {
      // First day: show instance start time to 11:59 PM
      return `${formatTime(instanceStart)} - 11:59 PM`;
    } else if (instanceDay === originalEndDay) {
      // Last day: show 12:00 AM to original end time
      return `12:00 AM - ${formatTime(originalEnd)}`;
    } else {
      // Middle days: show full day
      return '12:00 AM - 11:59 PM';
    }
  }

  // Single day: return formatted time range
  const startTime = formatTime(instanceStart);
  const endTime = formatTime(originalEnd);
  return `${startTime} - ${endTime}`;
};

export const SchEventContentRenderer = ({
  eventInfo,
  registerForUpdates = undefined,
  interviewDataRef,
  updateSharedData,
  removeSlot = undefined,
  onSelect = undefined,
  timezone,
}) => {
  const { event } = eventInfo;

  const type = event.extendedProps.type;

  const { bookedSlot } = interviewDataRef.current;

  const commonProps = {
    eventInfo,
    registerForUpdates,
    interviewDataRef,
    updateSharedData,
    timezone,
  };

  switch (type) {
    case 'manual-available':
      return (
        <ManualAvailableContent
          {...commonProps}
          createNewEvent={(obj) => {
            updateSharedData('bookedSlot', { ...bookedSlot, ...obj });
          }}
        />
      );
    case 'free-slot':
      return <SelfFreeSlot removeSlot={removeSlot} {...commonProps} />;
    case 'busy':
      return (
        <BusyEventContent
          onSelect={onSelect}
          interviewDataRef={interviewDataRef}
          {...commonProps}
        />
      );
    case 'booked':
      return <ManualBookedContent {...commonProps} />;
    case 'meeting_room':
      return <MeetingRoomEventContent {...commonProps} />;
    case 'candidate-availability': {
      const { candidateName } = event.extendedProps || {};
      const formattedTimeRange = formatTimeRangeForDay(event, eventInfo);

      return (
        <ManualAvailableContent
          {...commonProps}
          createNewEvent={(obj) => {
            updateSharedData('bookedSlot', { ...bookedSlot, ...obj });
          }}
          slotContent={({ noOfLines, isNarrow }) =>
            isNarrow ? null : (
              <>
                <KText
                  size="xs-semi-bold"
                  color="pastel-2-dark"
                  noOfLines={Math.max(1, noOfLines - 1)}
                >
                  {formattedTimeRange}
                </KText>
                {candidateName && (
                  <KText
                    size="xs-semi-bold"
                    color="pastel-2-dark"
                    lineHeight="1.3"
                    noOfLines={1}
                  >
                    {candidateName}
                  </KText>
                )}
              </>
            )
          }
          tooltipLabel={
            <Flex flexDir="column">
              <Box as="p" fontWeight="600">
                Available
              </Box>
              <Box as="p">{formattedTimeRange}</Box>
              {candidateName && <Box as="p">{candidateName}</Box>}
            </Flex>
          }
          contentBoxProps={{ p: '2px 4px' }}
        />
      );
    }
    default:
      return null;
  }
};
